import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { generateUniqId } from 'helpers/commonHelpers';
import { AdditionalFieldTypes } from 'enums/onboarding/crm';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { formatFromJSON, formatToJSON } from 'helpers/crmHelpers';
import { serviceProviderStepValidationSchema } from 'validations/intake-form/intake-form-steps';
import { AdditionalFieldFormItemWithNullableIdModel } from 'components/Forms/FormComponents/AdditionalField';
import {
  ServiceProviderFieldFromQuery,
  ServiceProviderFromQuery,
} from 'api/crm/intakeFormAPI';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { AutocompleteOption, Form, SectionIntro } from '@ui';

export type ServiceProviderFormItem = {
  id: string;
  serviceProvider: string | null;
  description: string;
  serviceProviderInitialValue?: AutocompleteOption<ServiceProviderFromQuery>;
  additionalFields: AdditionalFieldFormItemWithNullableIdModel[];
};

export interface FormValuesModel extends SubmitButtonsFormValues {
  serviceProviders: ServiceProviderFormItem[];
  requiredServiceProviders: ServiceProviderFormItem[];
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const ServiceProviders = ({
  disabled,
  renderNavigateButtonInTitle,
}: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    updateIntakeFormFields,
    navigateToPreviousStep,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }

    const formatAdditionalFields = (
      serviceProvider: ServiceProviderFromQuery,
      serviceProviderField?: ServiceProviderFieldFromQuery,
    ): AdditionalFieldFormItemWithNullableIdModel[] => {
      return serviceProvider.additionalFields
        .filter(
          ({ relatedTo }) => relatedTo === 'any' || relatedTo === 'contact',
        )
        .map((e) => {
          const predefinedAF = serviceProviderField
            ? serviceProviderField.additionalFields.find(
                (rAF) => rAF.name === e.name,
              )
            : null;

          let formattedValue = predefinedAF
            ? formatFromJSON(e.type, predefinedAF.valueJSON)
            : undefined;

          if (
            [AdditionalFieldTypes.Form, AdditionalFieldTypes.Document].includes(
              e.type,
            ) &&
            formattedValue
          ) {
            if (formattedValue && Array.isArray(formattedValue)) {
              formattedValue = formattedValue.map((e) => {
                const document = intakeForm.metadata.documents[e];
                if (document) {
                  return {
                    id: document.id,
                    name: document.files[0].name,
                    fileId: document.files[0].id,
                    file: document.files[0],
                  };
                }
              });
            }
          }

          return {
            id: predefinedAF?.id || null,
            name: e.name,
            description: e.description,
            type: e.type,
            value: formattedValue,
            relatedTo: e.relatedTo,
            options: {
              onboardingRequired: e.options.onboardingRequired,
              isForRegulatedOnly: e.options.isForRegulatedOnly,
              documentRequiresCertification:
                e.options.documentRequiresCertification,
              selectOptions: e.options.selectOptions.length
                ? e.options.selectOptions.map((e) => JSON.parse(e.valueJSON))
                : [],
              formDocument: e.options.formDocument,
            },
          };
        });
    };

    let requiredServiceProviders: ServiceProviderFormItem[] = [];
    if (intakeForm.metadata.requiredServiceProviderIds.length) {
      requiredServiceProviders =
        intakeForm.metadata.requiredServiceProviderIds.reduce<
          ServiceProviderFormItem[]
        >((acc, next) => {
          const initialServiceProvider =
            intakeForm.metadata.serviceProviders[next];

          if (initialServiceProvider) {
            const filledServiceProvider = intakeForm.fields.serviceProviders
              ? intakeForm.fields.serviceProviders.find((e) => e.id === next)
              : undefined;

            acc.push({
              id: next,
              serviceProvider: initialServiceProvider._id,
              description: initialServiceProvider.description,
              serviceProviderInitialValue: {
                id: initialServiceProvider._id,
                label: initialServiceProvider.name,
                model: initialServiceProvider,
              },
              additionalFields: formatAdditionalFields(
                initialServiceProvider,
                filledServiceProvider,
              ),
            });
          }

          return acc;
        }, []);
    }

    const otherServiceProviders =
      intakeForm.fields.serviceProviders &&
      Array.isArray(intakeForm.fields.serviceProviders)
        ? intakeForm.fields.serviceProviders.filter(
            (e) =>
              !intakeForm.metadata.requiredServiceProviderIds.includes(
                e.id as string,
              ),
          )
        : [];

    return {
      submitType: null,
      requiredServiceProviders,
      serviceProviders: otherServiceProviders.length
        ? otherServiceProviders.map((e) => {
            const initialServiceProvider =
              e.id &&
              e.serviceProviderId &&
              intakeForm.metadata.serviceProviders[e.id]
                ? intakeForm.metadata.serviceProviders[e.id]
                : null;

            return {
              id: e.id || generateUniqId(),
              serviceProvider: e.serviceProviderId || null,
              description: initialServiceProvider?.description || '',
              serviceProviderInitialValue: initialServiceProvider
                ? {
                    id: initialServiceProvider._id,
                    label: initialServiceProvider.name,
                    model: initialServiceProvider,
                  }
                : undefined,
              additionalFields: initialServiceProvider
                ? formatAdditionalFields(initialServiceProvider, e)
                : [],
            };
          })
        : !requiredServiceProviders.length
          ? [
              {
                id: `custom-${generateUniqId()}`,
                serviceProvider: null,
                description: '',
                additionalFields: [],
              },
            ]
          : [],
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }

    const filledServiceProviders =
      values.serviceProviders.filter((e) => !!e.serviceProvider) || [];

    if (values.requiredServiceProviders.length) {
      filledServiceProviders.push(...values.requiredServiceProviders);
    }

    await updateIntakeFormFields(
      {
        serviceProviders: filledServiceProviders.length
          ? filledServiceProviders.map((e) => ({
              id: e.id.includes('custom-') ? null : e.id,
              serviceProviderId: e.serviceProvider || null,
              additionalFields: e.additionalFields.map((af) => ({
                id: af.id,
                name: af.name,
                type: af.type,
                valueJSON: formatToJSON(af.type, af.value),
              })),
            }))
          : null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleVariant={disabled ? 'h5' : undefined}
        titleColor={colorsTheme.colorWhite}
        title={
          <DivAlignCenter>
            {t('intake_form.service_providers.title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="service_providers" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={serviceProviderStepValidationSchema}
      />
    </>
  );
};

export default ServiceProviders;
